import React, { useState, useEffect, useRef, useCallback } from 'react'
import useSWR from 'swr'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Snackbar,
  Alert,
  styled,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material'
import {
  CampaignSetupFormValues,
  ContactQuestion,
  CreativeType,
  DraftIds,
  SocialMediaAsset,
  QUESTION_TYPE_LABELS,
  PREDEFINED_QUESTION_KEYS,
} from './types'
import PlatformStep from './steps/PlatformStep'
import CampaignStep from './steps/CampaignStep'
import AdSetStep from './steps/AdSetStep'
import MediaStep from './steps/MediaStep'
import LeadFormStep from './steps/LeadFormStep'
import AdCreativeStep from './steps/AdCreativeStep'
import SummaryStep from './steps/SummaryStep'
import ResultStep from './steps/ResultStep'
import axiosInstance from '../../../../utils/axiosInstance'

// The UI names creative types the way Meta's docs do; the backend stores the shorter
// SocialCreative.format enum. Keep both directions in one place — a creative saved as
// video must load back as video, or switching steps silently rewrites it to an image.
const CREATIVE_TYPE_TO_FORMAT: Record<CreativeType, string> = {
  SINGLE_IMAGE: 'image',
  SINGLE_VIDEO: 'video',
  CAROUSEL: 'carousel',
}

const FORMAT_TO_CREATIVE_TYPE: Record<string, CreativeType> = {
  image: 'SINGLE_IMAGE',
  video: 'SINGLE_VIDEO',
  carousel: 'CAROUSEL',
}

const DEFAULT_QUESTIONS: ContactQuestion[] = [
  { id: 'q1', type: 'FULL_NAME', required: true },
  { id: 'q2', type: 'EMAIL', required: true },
  { id: 'q3', type: 'PHONE_NUMBER', required: false },
]

const getTodayLocal = () => {
  const d = new Date()
  d.setSeconds(0, 0)
  // Format for datetime-local input: "YYYY-MM-DDTHH:mm"
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`
}

const get14DaysFromNow = () => {
  const d = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`
}

const INITIAL_VALUES: CampaignSetupFormValues = {
  platforms: ['meta'],
  adAccountId: '',
  socialPageId: '',
  campaignName: '',
  campaignObjective: 'LEAD_GENERATION',
  campaignStatus: 'PAUSED',
  specialAdCategory: 'EMPLOYMENT',
  adSetName: '',
  performanceGoal: 'LINK_CLICKS',
  adSetStatus: 'PAUSED',
  destinationType: 'ON_AD',
  startTime: null,
  budgetType: 'daily_budget',
  endTime: null,
  amount: 6.4,
  targetingCities: [],
  targetingAgeMin: 18,
  targetingAgeMax: 65,
  placements: 'ADVANTAGE_PLUS',
  bidStrategy: 'LOWEST_COST_WITHOUT_CAP',
  mediaAssets: [],
  leadFormName: '',
  privacyPolicyUrl: '',
  introHeadline: '',
  introDescription: '',
  contextCardStyle: 'PARAGRAPH_STYLE',
  questionPageCustomHeadline: '',
  contactQuestions: DEFAULT_QUESTIONS,
  thankYouTitle: 'Thank you!',
  thankYouButtonUrl: '',
  thankYouButtonText: 'Visit Website',
  thankYouBody: 'We have received your application and will get back to you shortly.',
  adName: '',
  creativeType: 'SINGLE_IMAGE',
  primaryText: '',
  headline: '',
  description: '',
  websiteUrl: '',
  ctaType: 'APPLY_NOW',
}

const EMPTY_DRAFT_IDS: DraftIds = {
  smId: null,
  campaignId: null,
  adSetId: null,
  leadFormId: null,
  creativeId: null,
  adId: null,
}

const InnerTabsWrapper = styled(Box)({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  padding: '4px 0',
})

const InnerTabButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  padding: '12px 16px',
  cursor: 'pointer',
  borderRadius: '4px',
  backgroundColor: active ? theme.palette.primary.main : '#eeeeee',
  color: active ? '#ffffff' : theme.palette.text.secondary,
  fontWeight: active ? 'bold' : 'normal',
  flex: 1,
  minWidth: '120px',
  margin: '0 8px',
  textAlign: 'center',
  boxShadow: active ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : '#e0e0e0',
  },
}))

const getVisibleTabs = (objective: string) => {
  const tabs = [
    'Platform',
    'Campaign',
    'Ad Set',
    'Media',
    'Ad Creative',
    'Summary',
    'Result',
  ] as string[]
  if (objective === 'LEAD_GENERATION') {
    tabs.splice(4, 0, 'Lead Form')
  }
  return tabs
}

const tabToSlug = (tab: string) => tab.toLowerCase().replace(/\s+/g, '-')
const slugToTab = (tabs: string[], slug: string) => tabs.find((t) => tabToSlug(t) === slug)

interface Props {
  orderId?: string
  orderDisplayId?: string
  jobName?: string
}

const extractDraftIds = (data: any): DraftIds => ({
  smId: data?.id || null,
  campaignId: data?.campaigns?.[0]?.id || null,
  adSetId: data?.adSets?.[0]?.id || null,
  leadFormId: data?.leadForms?.[0]?.id || null,
  creativeId: data?.creatives?.[0]?.id || null,
  adId: data?.ads?.[0]?.id || null,
})

const mapResponseToValues = (data: any): Partial<CampaignSetupFormValues> => {
  const campaign = data?.campaigns?.[0]
  const adSet = data?.adSets?.[0]
  const leadForm = data?.leadForms?.[0]
  const creative = data?.creatives?.[0]

  return {
    platforms: data?.platform ? [data.platform] : ['meta'],
    ...(data?.adAccount?.id ? { adAccountId: data.adAccount.id } : {}),
    ...(data?.socialPage?.id ? { socialPageId: data.socialPage.id } : {}),

    campaignName: campaign?.name || '',
    adSetName: adSet?.name || '',

    campaignObjective:
      campaign?.objective === 'leadGeneration'
        ? 'LEAD_GENERATION'
        : campaign?.objective === 'outcomeTraffic'
        ? 'OUTCOME_TRAFFIC'
        : 'LEAD_GENERATION',
    campaignStatus: 'PAUSED',
    specialAdCategory: campaign?.specialAdCategories?.[0] || 'EMPLOYMENT',

    performanceGoal: (adSet?.optimizationGoal as any) || 'LINK_CLICKS',
    adSetStatus: 'PAUSED',
    destinationType: adSet?.destinationType === 'onAd' ? 'ON_AD' : 'WEBSITE',
    budgetType: adSet?.budgetType === 'lifetime' ? 'lifetime_budget' : 'daily_budget',
    amount: adSet?.budget ? Number(adSet.budget) : 6.4,
    startTime: adSet?.startTime
      ? new Date(adSet.startTime).toISOString().slice(0, 16)
      : getTodayLocal(),
    ...(adSet?.endTime ? { endTime: new Date(adSet.endTime).toISOString().slice(0, 16) } : {}),
    targetingCities: ((adSet?.targeting as any)?.geo_locations?.cities || []).map((c: any) => ({
      key: c.key,
      name: c.name,
      region: c.region,
      countryCode: c.country_code,
      radius: c.radius ?? 40,
    })),
    targetingAgeMin: adSet?.ageMin || 18,
    targetingAgeMax: adSet?.ageMax || 65,
    placements: adSet?.placementMode === 'advantage' ? 'ADVANTAGE_PLUS' : 'MANUAL',
    bidStrategy: (adSet?.bidStrategy as any) || 'LOWEST_COST_WITHOUT_CAP',

    leadFormName: leadForm?.name || '',
    privacyPolicyUrl: leadForm?.privacyPolicyUrl || '',
    introHeadline: (leadForm?.contextCard as any)?.title || '',
    introDescription: (leadForm?.contextCard as any)?.content || '',
    contextCardStyle: (leadForm?.contextCardStyle as any) || 'PARAGRAPH_STYLE',
    questionPageCustomHeadline: (leadForm as any)?.questionPageCustomHeadline || '',
    contactQuestions: leadForm?.questions
      ? (leadForm.questions as any[]).map((q: any) => ({
          id: q.key || `loaded_${Math.random().toString(36).slice(2)}`,
          key: q.key,
          type: q.type as ContactQuestion['type'],
          required: true,
          label: q.label,
          isCustom: q.type === 'CUSTOM',
        }))
      : DEFAULT_QUESTIONS,
    thankYouTitle: (leadForm?.thankYouPage as any)?.title || 'Thank you!',
    thankYouBody:
      (leadForm?.thankYouPage as any)?.body ||
      'We have received your application and will get back to you shortly.',
    thankYouButtonUrl: leadForm?.followUpActionUrl || '',
    thankYouButtonText: (leadForm as any)?.thankYouButtonText || 'Visit Website',

    adName: creative?.name || '',
    creativeType: FORMAT_TO_CREATIVE_TYPE[creative?.format as string] || 'SINGLE_IMAGE',
    primaryText: creative?.body || '',
    headline: creative?.title || '',
    description: creative?.description || '',
    websiteUrl: creative?.linkUrl || '',
    ctaType: (creative?.callToAction as any) || 'APPLY_NOW',

    mediaAssets: (data?.mediaAssets || []).map(
      (a: any): SocialMediaAsset => ({
        id: a.id,
        name: a.name,
        type: a.type,
        placement: a.placement || undefined,
        image: a.image
          ? { url: a.image.url, width: a.image.width, height: a.image.height }
          : undefined,
        file: a.file ? { url: a.file.url, filename: a.file.filename } : undefined,
      })
    ),
  }
}

const CampaignSetup: React.FC<Props> = ({ orderId, orderDisplayId, jobName }) => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [activeStep, setActiveStep] = useState(0)
  const { data: orderData } = useSWR<any>(orderId ? `/orders/${orderId}` : null, axiosInstance)
  const {
    data: smData,
    isLoading: smLoading,
    mutate: mutateSmData,
  } = useSWR<any>(orderId ? `/api/social-marketing/by-order/${orderId}` : null, axiosInstance, {
    onErrorRetry: (err, _key, _config, revalidate, { retryCount }) => {
      if (err?.response?.status === 404) return
      if (retryCount >= 3) return
      setTimeout(() => revalidate({ retryCount }), 5000)
    },
  })
  const customerId = orderData?.data?.order?.customer?.id
  const [draftLoaded, setDraftLoaded] = useState(!orderId)
  const [values, setValues] = useState<CampaignSetupFormValues>(() => ({
    ...INITIAL_VALUES,
    startTime: getTodayLocal(),
    endTime: get14DaysFromNow(),
  }))

  const [draftIds, setDraftIds] = useState<DraftIds>(EMPTY_DRAFT_IDS)
  const [isSaving, setIsSaving] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })
  const [errorDialog, setErrorDialog] = useState<{ open: boolean; detail: string }>({
    open: false,
    detail: '',
  })

  const draftIdsRef = useRef<DraftIds>(EMPTY_DRAFT_IDS)
  const draftLoadedRef = useRef(!orderId)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const valuesRef = useRef<CampaignSetupFormValues>(values)

  useEffect(() => {
    draftIdsRef.current = draftIds
  }, [draftIds])

  useEffect(() => {
    valuesRef.current = values
  }, [values])

  const buildPrefillNames = (order: any) => {
    const date = new Date()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    const id = order.displayId
    const externalImportId = order.externalImportId || ''
    const customerName = order.customer?.name || ''
    const job = order.jobName || ''
    const campaignName = [`${month} ${year}`, externalImportId, id, customerName, job]
      .filter(Boolean)
      .join(' - ')
    const adName = `${id} - image ad`
    return { campaignName, adSetName: campaignName, leadFormName: campaignName, adName }
  }

  // Load existing draft when smData arrives
  useEffect(() => {
    if (smLoading) return
    if (!smData) {
      // No existing draft — wait for orderData too before prefilling
      const order = orderData?.data?.order
      if (orderId && !order) return
      if (order) {
        const prefill = buildPrefillNames(order)
        setValues((prev) => ({ ...prev, ...prefill }))
        valuesRef.current = { ...valuesRef.current, ...prefill }
      }
      draftLoadedRef.current = true
      setDraftLoaded(true)
      return
    }
    const data = smData.data
    const ids = extractDraftIds(data)
    setDraftIds(ids)
    draftIdsRef.current = ids
    const mapped = mapResponseToValues(data)
    setValues((prev) => ({ ...prev, ...mapped }))
    valuesRef.current = { ...valuesRef.current, ...mapped }
    draftLoadedRef.current = true
    setDraftLoaded(true)
  }, [smData, smLoading, orderData, orderId])

  // Restore active step from URL after draft is loaded
  useEffect(() => {
    if (!draftLoaded) return
    const tabs = getVisibleTabs(valuesRef.current.campaignObjective)
    const tabParam = [...searchParams.keys()][0]
    if (tabParam) {
      const matched = slugToTab(tabs, tabParam)
      const idx = matched ? tabs.indexOf(matched) : -1
      if (idx >= 0) {
        setActiveStep(idx)
        return
      }
    }
    // No explicit tab in the URL — published campaigns open straight on Result,
    // since the other steps are now read-only and Result is what the user came to see.
    if (smData?.data?.status === 'published') {
      const resultIdx = tabs.indexOf('Result')
      if (resultIdx >= 0) setActiveStep(resultIdx)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftLoaded])

  const buildPayload = useCallback(
    (currentValues: CampaignSetupFormValues, ids: DraftIds) => {
      return {
        name: `${currentValues.platforms[0] || 'meta'} - ${
          jobName || orderDisplayId || 'Campaign'
        }`,
        platform: currentValues.platforms[0] || 'meta',
        orderId,
        ...(currentValues.adAccountId && { adAccountId: currentValues.adAccountId }),
        ...(currentValues.socialPageId && { socialPageId: currentValues.socialPageId }),
        campaigns: [
          {
            ...(ids.campaignId ? { id: ids.campaignId } : {}),
            name: currentValues.campaignName || 'Campaign',
            objective:
              currentValues.campaignObjective === 'LEAD_GENERATION'
                ? 'leadGeneration'
                : 'outcomeTraffic',
            specialAdCategories:
              currentValues.specialAdCategory !== 'NONE'
                ? [currentValues.specialAdCategory]
                : ['EMPLOYMENT'],
            status: 'paused',
          },
        ],
        adSets: [
          {
            ...(ids.adSetId ? { id: ids.adSetId } : {}),
            name: currentValues.adSetName || 'Ad Set',
            ...(currentValues.amount !== '' && { budget: currentValues.amount }),
            budgetType: currentValues.budgetType === 'daily_budget' ? 'daily' : 'lifetime',
            ageMin: currentValues.targetingAgeMin,
            ageMax: currentValues.targetingAgeMax,
            targeting: {
              geo_locations:
                currentValues.targetingCities.length > 0
                  ? {
                      cities: currentValues.targetingCities.map((c) => ({
                        key: c.key,
                        name: c.name,
                        radius: c.radius,
                        distance_unit: 'kilometer',
                      })),
                      location_types: ['home', 'recent'],
                    }
                  : { countries: ['DE'] },
            },
            optimizationGoal: currentValues.performanceGoal,
            billingEvent: 'IMPRESSIONS',
            destinationType: currentValues.destinationType === 'ON_AD' ? 'onAd' : 'website',
            advantageAudience: true,
            placementMode: 'advantage',
            bidStrategy: currentValues.bidStrategy,
            ...(currentValues.startTime && {
              startTime: new Date(currentValues.startTime).toISOString(),
            }),
            ...(currentValues.endTime && {
              endTime: new Date(currentValues.endTime).toISOString(),
            }),
            status: 'paused',
          },
        ],
        ...(currentValues.campaignObjective === 'LEAD_GENERATION' && {
          leadForms: [
            {
              ...(ids.leadFormId ? { id: ids.leadFormId } : {}),
              name: currentValues.leadFormName || `Lead Form - ${jobName || 'Campaign'}`,
              questions: currentValues.contactQuestions.map((q) => ({
                key: q.type === 'CUSTOM' ? q.key || q.id : PREDEFINED_QUESTION_KEYS[q.type],
                label: q.label || QUESTION_TYPE_LABELS[q.type] || q.type,
                type: q.type,
              })),
              ...(currentValues.privacyPolicyUrl && {
                privacyPolicyUrl: currentValues.privacyPolicyUrl,
              }),
              locale: 'en_US',
              ...(currentValues.thankYouButtonUrl && {
                followUpActionUrl: currentValues.thankYouButtonUrl,
              }),
              contextCardStyle: currentValues.contextCardStyle,
              ...(currentValues.introHeadline && {
                contextCard: {
                  title: currentValues.introHeadline,
                  content: currentValues.introDescription,
                },
              }),
              ...(currentValues.questionPageCustomHeadline && {
                questionPageCustomHeadline: currentValues.questionPageCustomHeadline,
              }),
              thankYouPage: {
                title: currentValues.thankYouTitle,
                body: currentValues.thankYouBody,
              },
              thankYouButtonText: currentValues.thankYouButtonText,
            },
          ],
        }),
        creatives: [
          {
            ...(ids.creativeId ? { id: ids.creativeId } : {}),
            name: currentValues.adName || `Creative - ${jobName || 'Campaign'}`,
            format: CREATIVE_TYPE_TO_FORMAT[currentValues.creativeType],
            ...(currentValues.primaryText && { body: currentValues.primaryText }),
            ...(currentValues.headline && { title: currentValues.headline }),
            ...(currentValues.description && { description: currentValues.description }),
            callToAction: currentValues.ctaType,
            ...(currentValues.websiteUrl && { linkUrl: currentValues.websiteUrl }),
            status: 'paused',
          },
        ],
        ads: [
          {
            ...(ids.adId ? { id: ids.adId } : {}),
            name: currentValues.adName || `Ad - ${jobName || 'Campaign'}`,
            status: 'paused',
          },
        ],
      }
    },
    [orderId, orderDisplayId, jobName]
  )

  const saveDraft = useCallback(
    async (currentValues: CampaignSetupFormValues) => {
      if (!orderId || !draftLoadedRef.current) return
      if (smData?.data?.status === 'published') return
      const ids = draftIdsRef.current
      setIsSaving(true)
      try {
        const payload = buildPayload(currentValues, ids)
        if (!ids.smId) {
          const res = await axiosInstance.post('/api/social-marketing', payload)
          const newIds = extractDraftIds(res.data)
          setDraftIds(newIds)
          draftIdsRef.current = newIds
          // Force SWR to re-fetch (clears 404 error cache)
          mutateSmData()
        } else {
          await axiosInstance.put(`/api/social-marketing/${ids.smId}`, payload)
        }
      } catch {
        // silent fail on auto-save
      } finally {
        setIsSaving(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderId, buildPayload, mutateSmData, smData?.data?.status]
  )

  const handleChange = (field: keyof CampaignSetupFormValues, value: unknown) => {
    const newValues = { ...valuesRef.current, [field]: value }
    setValues(newValues)
    valuesRef.current = newValues

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => saveDraft(newValues), 800)
  }

  const handleStepChange = useCallback(
    (newStep: number) => {
      const tabs = getVisibleTabs(valuesRef.current.campaignObjective)
      const clamped = Math.max(0, Math.min(newStep, tabs.length - 1))
      // Flush any pending debounced save
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
        saveDraft(valuesRef.current)
      }
      setActiveStep(clamped)
      navigate({ search: `?${tabToSlug(tabs[clamped])}` }, { replace: true })
    },
    [navigate, saveDraft]
  )

  const goNext = async () => {
    const tabs = getVisibleTabs(valuesRef.current.campaignObjective)
    // Flush any pending debounced save immediately before navigating
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    await saveDraft(valuesRef.current)
    // NOTE: we deliberately do NOT connect the chosen ad account / page to the customer
    // here any more. Ad accounts and pages are shared agency assets, but their `customer`
    // relation is a single nullable FK, so connecting REASSIGNED the asset away from
    // whichever customer held it before. Doing that on every step advance silently made
    // accounts disappear from other customers' dropdowns in production.
    //
    // The selection is already persisted on the draft itself (SocialMarketing.adAccountId
    // / .socialPageId) by saveDraft above, which is what publishing actually reads.
    handleStepChange(Math.min(activeStep + 1, tabs.length - 1))
  }
  const goBack = () => handleStepChange(Math.max(activeStep - 1, 0))

  const handlePublish = async () => {
    const smId = draftIdsRef.current.smId
    if (!smId) {
      setSnackbar({
        open: true,
        message: 'No draft to publish. Please fill in the form first.',
        severity: 'error',
      })
      return
    }
    setIsPublishing(true)
    try {
      await axiosInstance.post('/api/social-marketing/publish', { socialMarketingId: smId })
      setSnackbar({ open: true, message: 'Campaign published successfully!', severity: 'success' })
      await mutateSmData()
      const tabs = getVisibleTabs(valuesRef.current.campaignObjective)
      const resultIdx = tabs.indexOf('Result')
      if (resultIdx >= 0) setActiveStep(resultIdx)
    } catch (err: any) {
      const data = err?.response?.data || {}
      const msg = data.message || 'Failed to publish. Please check all required fields.'
      const errors: string[] = data.errors || []
      const detail = JSON.stringify(
        {
          message: msg,
          ...(errors.length ? { errors } : {}),
          ...(data.socialMarketingId ? { socialMarketingId: data.socialMarketingId } : {}),
          ...(data.partialResources?.length ? { partialResources: data.partialResources } : {}),
          ...(data.error ? { serverError: data.error } : {}),
        },
        null,
        2
      )
      setErrorDialog({ open: true, detail })
    } finally {
      setIsPublishing(false)
    }
  }

  const visibleTabs = getVisibleTabs(values.campaignObjective)
  const isPublished = smData?.data?.status === 'published'

  const renderStep = () => {
    const tabName = visibleTabs[activeStep]
    switch (tabName) {
      case 'Platform':
        return (
          <PlatformStep
            platforms={values.platforms}
            adAccountId={values.adAccountId}
            socialPageId={values.socialPageId}
            customerId={customerId}
            onChange={handleChange}
            onNext={goNext}
            onBack={goBack}
            isFirstStep
            readOnly={isPublished}
          />
        )
      case 'Campaign':
        return (
          <CampaignStep
            values={values}
            onChange={handleChange}
            onNext={goNext}
            onBack={goBack}
            readOnly={isPublished}
          />
        )
      case 'Ad Set':
        return (
          <AdSetStep
            values={values}
            onChange={handleChange}
            onNext={goNext}
            onBack={goBack}
            readOnly={isPublished}
          />
        )
      case 'Media': {
        const order = orderData?.data?.order
        return (
          <MediaStep
            smId={draftIdsRef.current.smId}
            mediaAssets={values.mediaAssets}
            onAssetsChange={(assets: SocialMediaAsset[]) => handleChange('mediaAssets', assets)}
            onNext={goNext}
            onBack={goBack}
            orderData={{
              jobName: order?.jobName || jobName,
              company: order?.customer?.name,
              city: order?.place || order?.city,
              employmentType: order?.employmentType,
              logoUrl: order?.customer?.logoUrl,
              headerImage: order?.headerImage,
            }}
            readOnly={isPublished}
          />
        )
      }
      case 'Lead Form':
        return (
          <LeadFormStep
            values={values}
            onChange={handleChange}
            onNext={goNext}
            onBack={goBack}
            readOnly={isPublished}
          />
        )
      case 'Ad Creative':
        return (
          <AdCreativeStep
            values={values}
            onChange={handleChange}
            onNext={goNext}
            onBack={goBack}
            jobName={jobName}
            readOnly={isPublished}
          />
        )
      case 'Summary':
        return (
          <SummaryStep
            values={values}
            draftId={draftIdsRef.current.smId}
            onBack={goBack}
            onSubmit={handlePublish}
            isPublishing={isPublishing}
            orderDisplayId={orderDisplayId}
            jobName={jobName}
            readOnly={isPublished}
          />
        )
      case 'Result': {
        const smRaw = smData?.data
        return (
          <ResultStep
            status={smRaw?.status || null}
            createdAt={smRaw?.createdAt || null}
            updatedAt={smRaw?.updatedAt || null}
            adAccountExternalId={smRaw?.adAccount?.externalId || null}
            campaignExternalId={smRaw?.campaigns?.[0]?.externalId || null}
          />
        )
      }
      default:
        return null
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Inner sub-tab navigation */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          mb: 3,
          padding: '12px 0',
        }}
      >
        <InnerTabsWrapper>
          {visibleTabs.map((tab, index) => (
            <InnerTabButton
              key={tab}
              active={activeStep === index}
              onClick={() => handleStepChange(index)}
            >
              {tab}
            </InnerTabButton>
          ))}
        </InnerTabsWrapper>
      </Box>

      <Box sx={{ px: 1 }}>{renderStep()}</Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={errorDialog.open}
        onClose={() => setErrorDialog((s) => ({ ...s, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Publish failed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Copy and paste the details below for debugging:
          </Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.100',
              p: 2,
              borderRadius: 1,
              fontSize: 12,
              overflowX: 'auto',
              userSelect: 'all',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {errorDialog.detail}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialog((s) => ({ ...s, open: false }))}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CampaignSetup
